import { Action, ThunkDispatch, createAsyncThunk } from '@reduxjs/toolkit'
import { ProjectStateAsString } from 'controllers/Project/ProjectStateMachine'
import { ConfirmationDialogProps } from 'features/ConfirmationDialog/ConfirmationDialog'
import i18next from 'i18next'
import { AssemblyHeaderRow, BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { ShowException } from 'store/Application/appActions'
import { uiActions } from 'store/UI/UIReducer'
import { RootAppState } from 'store/configureStore'
import { newProjectActions } from '../projectReducer'
import { bomItemSelector } from '../selectors/bomItemSelector'
import { projectSelectors } from '../selectors/projectSelectors'
import { projectAPIBuilder } from './BoMAsyncActions'
import { fetchProject } from './fetchProject'

type ProjectAPIBuilder = ReturnType<typeof projectAPIBuilder>

async function deleteBomItems(
  bomItemPointers: BomItemPointer[],
  dispatch: ThunkDispatch<RootAppState, unknown, Action<string>>,
  projectAPI: ProjectAPIBuilder,
) {
  try {
    const _pointers = bomItemPointers.filter((x) => Boolean(x?.id))

    // client side delete
    dispatch(newProjectActions.deleteBomItemsFromStore(_pointers))

    // server side delete
    const ret = await projectAPI.api.DeleteRowOrAssembly(
      projectAPI.projectId,
      _pointers.map((x) => x?.id),
    )

    return ret
  } catch (err) {
    dispatch(fetchProject({ projectId: projectAPI.projectId }))
    ShowException('project', err)
    throw err
  } finally {
    dispatch(uiActions.closeModal())
  }
}

/**
 * Delete a BOM item from local store and server
 * if multiple BOM items are selected, delete all of them after confirmation
 * if one of the filters is active, delete the visible part types after confirmation
 * if no filters and is an assembly header, delete all the children after confirmation
 * @param bomItemPointer The pointer to the BOM item to delete
 */
export const deleteBoMItem = createAsyncThunk<
  void,
  { bomItemPointer?: BomItemPointer; onDeleted?: () => void },
  { state: RootAppState }
>('boM/deleteBoMItem', async ({ bomItemPointer, onDeleted }, thunkAPI) => {
  const projectAPI = projectAPIBuilder(thunkAPI.getState)
  try {
    let bomItemsToDelete: BomItemPointer[] = []
    let affectedItemsCount = 1
    let confirmText: string = null

    const selectedBomItems = projectSelectors.selectedBomItemsSelector(
      thunkAPI.getState(),
    )

    if (selectedBomItems?.length === 0 && bomItemPointer) {
      const bomItem = bomItemSelector(thunkAPI.getState(), bomItemPointer)

      switch (bomItem?.type) {
        case BomItemType.project: {
          console.error('use deleteProject to delete the project')
          break
        }
        case BomItemType.routingHeader:
        case BomItemType.materialHeader:
        case BomItemType.assemblyType:
          // if filters are active, delete just the visible part types
          // if no filters are active, delete the header only
          if (
            bomItem.filteredPartTypePointers.length ===
            bomItem.partTypePointers.length
          ) {
            bomItemsToDelete = [bomItemPointer]
            affectedItemsCount = bomItem.partTypePointers.length
            confirmText = i18next.t('project:delete-assembly-desc', {
              defaultValue: 'Do you want to delete the assembly "{{name}}"?',
              name: (bomItem as AssemblyHeaderRow).name,
            })
          } else {
            bomItemsToDelete = bomItem.filteredPartTypePointers
            affectedItemsCount = bomItem.filteredPartTypePointers.length
          }
          break
        default: {
          bomItemsToDelete = [bomItemPointer]
          affectedItemsCount = 1

          const bomItemName = bomItem.name
          confirmText = i18next.t('project:delete-part-desc', {
            name: bomItemName,
          })
        }
      }
    } else {
      // if no bom item pointer is passed, delete all selected bom items
      const state = thunkAPI.getState()
      const selectedPointers =
        projectSelectors.selectedBomItemsPointersSelector(state)

      // Group selected partType items by their parent assembly
      // and detect directly selected assembly pointers
      const assemblySelectedParts: Record<string, BomItemPointer[]> = {}
      const directlySelectedAssemblies: BomItemPointer[] = []
      const otherItems: BomItemPointer[] = []

      selectedPointers.forEach((pointer) => {
        const item = bomItemSelector(state, pointer)
        if (
          item?.type === BomItemType.partType &&
          item?.parentBomItemPointer?.type === BomItemType.assemblyType
        ) {
          const parentId = item.parentBomItemPointer.id
          if (!assemblySelectedParts[parentId]) {
            assemblySelectedParts[parentId] = []
          }
          assemblySelectedParts[parentId].push(pointer)
        } else if (item?.type === BomItemType.assemblyType) {
          directlySelectedAssemblies.push(pointer)
        } else {
          otherItems.push(pointer)
        }
      })

      // Check which assemblies have ALL their part types selected
      const fullySelectedAssemblyPointers: BomItemPointer[] = [
        ...directlySelectedAssemblies,
      ]
      const partiallySelectedParts: BomItemPointer[] = []

      for (const [assemblyId, selectedParts] of Object.entries(
        assemblySelectedParts,
      )) {
        const assembly = bomItemSelector(state, {
          id: assemblyId,
          type: BomItemType.assemblyType,
        }) as AssemblyHeaderRow

        if (
          assembly &&
          assembly.partTypePointers?.length === selectedParts.length
        ) {
          // Avoid duplicates if the assembly is also directly selected
          if (!directlySelectedAssemblies.some((a) => a.id === assemblyId)) {
            fullySelectedAssemblyPointers.push({
              id: assemblyId,
              type: BomItemType.assemblyType,
            })
          }
        } else {
          partiallySelectedParts.push(...selectedParts)
        }
      }

      if (fullySelectedAssemblyPointers.length > 0) {
        // Ask user if they want to delete the entire assembly(ies)
        const assemblyNames = fullySelectedAssemblyPointers
          .map((p) => {
            const a = bomItemSelector(state, p) as AssemblyHeaderRow
            return a?.name
          })
          .filter(Boolean)
          .join(', ')

        const deleteAssemblyConfirmText = i18next.t(
          'project:delete-entire-assembly-confirmation',
          {
            defaultValue:
              'All rows of the following assemblies are selected: {{assemblyNames}}. Do you want to delete the entire assemblies?',
            count: fullySelectedAssemblyPointers.length,
            assemblyNames,
          },
        )

        return new Promise<void>((resolve) => {
          thunkAPI.dispatch(
            uiActions.openModal({
              modalType: 'ConfirmationDialog',
              modalProps: {
                title: deleteAssemblyConfirmText,
                onConfirm: async () => {
                  const itemsToDelete = [
                    ...fullySelectedAssemblyPointers,
                    ...partiallySelectedParts,
                    ...otherItems,
                  ]

                  // Clear selected items since we're replacing individual
                  // part type selections with assembly pointers for deletion
                  thunkAPI.dispatch(newProjectActions.clearSelectedBomItems())

                  const currentStatus =
                    thunkAPI.getState().project.activeProject?.status

                  if (
                    currentStatus &&
                    currentStatus !== ProjectStateAsString.DEFINING
                  ) {
                    try {
                      await projectAPI.api.SetProjectStatus(
                        projectAPI.projectId,
                        currentStatus.toUpperCase() as ProjectStateAsString,
                        ProjectStateAsString.DEFINING,
                      )
                    } catch (err) {
                      ShowException('project', err)
                      resolve()
                      return
                    }
                  }

                  await deleteBomItems(
                    itemsToDelete,
                    thunkAPI.dispatch,
                    projectAPI,
                  )
                  onDeleted?.()
                  resolve()
                },
                onCancel: () => {
                  resolve()
                  return Promise.resolve()
                },
              } as ConfirmationDialogProps,
            }),
          )
        })
      }

      bomItemsToDelete = selectedPointers
      affectedItemsCount = bomItemsToDelete.length
    }

    await executeDelete(
      bomItemsToDelete,
      affectedItemsCount,
      confirmText,
      thunkAPI,
      projectAPI,
      onDeleted,
    )
  } catch (err) {
    ShowException('project', err)
    throw err
  }
})

async function executeDelete(
  bomItemsToDelete: BomItemPointer[],
  affectedItemsCount: number,
  confirmText: string | null,
  thunkAPI: {
    getState: () => RootAppState
    dispatch: ThunkDispatch<RootAppState, unknown, Action<string>>
  },
  projectAPI: ProjectAPIBuilder,
  onDeleted?: () => void,
) {
  if (bomItemsToDelete.length === 0) {
    console.error('no bom item to delete')
    return
  }

  const deleteModalTitle =
    confirmText ??
    i18next.t('delete-parts-title', {
      count: affectedItemsCount,
      ns: 'project',
    })

  const deleteModal = () =>
    thunkAPI.dispatch(
      uiActions.openModal({
        modalType: 'ConfirmationDialog',
        modalProps: {
          title: deleteModalTitle,
          onConfirm: async () => {
            await deleteBomItems(
              bomItemsToDelete,
              thunkAPI.dispatch,
              projectAPI,
            )
            onDeleted?.()
          },
          onCancel: () => {
            return Promise.resolve()
          },
        } as ConfirmationDialogProps,
      }),
    )

  const currentStatus = thunkAPI.getState().project.activeProject?.status

  if (currentStatus && currentStatus !== ProjectStateAsString.DEFINING) {
    const localizedDefining = i18next
      .t(`project:state-${ProjectStateAsString.DEFINING.toLowerCase()}`)
      .toLocaleUpperCase()

    thunkAPI.dispatch(
      uiActions.openModal({
        modalType: 'ConfirmationDialog',
        modalProps: {
          title: deleteModalTitle,
          message: i18next.t('change-project-status-defining-and-delete', {
            defaultValue: `Delete parts is only allowed in {{localizedDefining}} state. Do you want to change the project status to {{localizedDefining}} and delete the items?`,
            localizedDefining: localizedDefining,
            ns: 'project',
          }),
          onConfirm: async () => {
            try {
              await projectAPI.api.SetProjectStatus(
                projectAPI.projectId,
                thunkAPI
                  .getState()
                  .project.activeProject.status.toUpperCase() as ProjectStateAsString,
                ProjectStateAsString.DEFINING,
              )

              await deleteBomItems(
                bomItemsToDelete,
                thunkAPI.dispatch,
                projectAPI,
              )

              onDeleted?.()
            } catch (err) {
              ShowException('project', err)
              throw err
            }
          },
        },
      }),
    )
  } else {
    deleteModal()
  }
}
